import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useCityContext } from '@contexts/LocationContext'
import { formatDateToDayMonthYear, formatCPF, formatCEP } from '@utils/formatters'
import { validateEmail, validatePassword, validateFullName, getValidationError } from '@utils/validators'
import { lookupCep } from '@utils/cep'
import { Button, Loading, Dropdown } from '@components'
import DatePickerInput from '@components/DatePickerInput';
import api from '@services/api'
import { ENABLE_GOOGLE_LOGIN, DISABLE_PASSPORT_REGISTRATION } from '@config/features'
import { DOCUMENT_TYPES, STATES_OPTIONS } from '@config/constants'
import './Register.css'

const Register = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signUp, signInWithGoogle, isAuthenticated, loading, error, clearError } = useAuth()
  const { city, isRibeirao } = useCityContext()

  const [formData, setFormData] = useState({
    document_type: 'CPF',
    main_document: '',
    name: '',
    birth_date: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  });
  const [disabledFields, setDisabledFields] = useState({
    name: true,
    birth_date: true,
    email: true,
    confirmEmail: true,
    password: true,
    confirmPassword: true
  });
  const [lastCheckedDocument, setLastCheckedDocument] = useState('');
  const [documentCheckMessage, setDocumentCheckMessage] = useState('');
  const [isCheckingDocument, setIsCheckingDocument] = useState(false);
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  // Redireciona se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Redireciona para o perfil ou para página anterior
      const fromPathname = location.state?.from?.pathname;
      const defaultPath = `/${city?.slug || ''}/perfil`;
      let redirectTo = defaultPath;

      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, location, city?.slug])

  // Limpa erros ao mudar de campo
  useEffect(() => {
    clearError()
  }, [formData, clearError])

  const handleDocumentCheck = async (directDocument = null) => {
    const docToVerify = directDocument || formData.main_document;

    // Se não tiver documento, ou já estiver checando, ou for o mesmo que o último checado, não faz nada
    if (!docToVerify || isCheckingDocument || docToVerify === lastCheckedDocument) return;

    // Se for CPF e não tiver 11 dígitos, não verifica
    if (formData.document_type === 'CPF' && docToVerify.length !== 11) return;

    setIsCheckingDocument(true);
    setDocumentCheckMessage('');
    setFormErrors({});

    try {
      const result = await api.checkOrCreateUser({
        document_type: formData.document_type,
        main_document: docToVerify
      });

      if (result.success) {
        setLastCheckedDocument(docToVerify);
        if (formData.document_type === 'CPF') {
          const user = result.data.user;
          setFormData(prev => ({
            ...prev,
            name: user.name,
            birth_date: user.birth_date ? formatDateToDayMonthYear(user.birth_date) : '',
          }));
          setDisabledFields({
            name: true,
            birth_date: true,
            email: false,
            confirmEmail: false,
            password: false,
            confirmPassword: false
          });
        } else { // Passaporte
          setDocumentCheckMessage('Seus dados serão conferidos posteriormente.');
          setDisabledFields({
            name: false,
            birth_date: false,
            email: false,
            confirmEmail: false,
            password: false,
            confirmPassword: false
          });
        }
      } else {
        // Erro da API (ex: menor de idade, CPF inválido)
        setLastCheckedDocument('');
        setFormErrors(prev => ({ ...prev, main_document: result.message }));
        setDisabledFields({
          name: true,
          birth_date: true,
          email: true,
          confirmEmail: true,
          password: true,
          confirmPassword: true
        });
      }
    } catch (error) {
      setLastCheckedDocument('');
      setFormErrors(prev => ({ ...prev, main_document: error.message || 'Erro ao verificar documento.' }));
      setDisabledFields({
        name: true,
        birth_date: true,
        email: true,
        confirmEmail: true,
        password: true,
        confirmPassword: true
      });
    } finally {
      setIsCheckingDocument(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'cep') {
      formattedValue = formatCEP(value)
    }

    if (name === 'main_document' && formData.document_type === 'CPF') {
      const cleaned = value.replace(/\D/g, '').slice(0, 11);
      formattedValue = cleaned;

      // Se completou 11 dígitos, verifica automaticamente
      if (cleaned.length === 11 && cleaned !== lastCheckedDocument) {
        // Usamos um timeout pequeno para garantir que o estado formData já foi atualizado ou passamos o valor direto
        setTimeout(() => handleDocumentCheck(cleaned), 100);
      }
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }))

    // Validação em tempo real apenas se já houver um erro exibido ou para limpar erros ao corrigir
    if (formErrors[name]) {
      if (name === 'email') {
        if (!formattedValue || validateEmail(formattedValue)) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
        }
      } else if (name === 'confirmEmail') {
        if (!formattedValue || formattedValue === formData.email) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.confirmEmail;
            return newErrors;
          });
        }
      } else if (name === 'password') {
        if (!formattedValue || validatePassword(formattedValue)) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.password;
            return newErrors;
          });
        }
      } else if (name === 'confirmPassword') {
        if (!formattedValue || formattedValue === formData.password) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.confirmPassword;
            return newErrors;
          });
        }
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }

    // Se o email mudar e o campo de confirmação já tiver erro, revalida a confirmação
    if (name === 'email' && formErrors.confirmEmail) {
      if (formattedValue === formData.confirmEmail) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmEmail;
          return newErrors;
        });
      }
    }

    // Se a senha mudar e o campo de confirmação já tiver erro, revalida a confirmação
    if (name === 'password' && formErrors.confirmPassword) {
      if (formattedValue === formData.confirmPassword) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    }

    // Se o tipo de documento ou o documento principal mudar, resetar os campos
    if (name === 'document_type' || (name === 'main_document' && value !== lastCheckedDocument)) {
      setFormData(prev => ({
        ...prev,
        name: '',
        birth_date: '',
        email: '',
        confirmEmail: '',
        password: '',
        confirmPassword: ''
      }));
      setDisabledFields({
        name: true,
        birth_date: true,
        email: true,
        confirmEmail: true,
        password: true,
        confirmPassword: true
      });
      setDocumentCheckMessage('');
      if (name === 'document_type') setLastCheckedDocument('');
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target

    if (name === 'email' && value) {
      if (!validateEmail(value)) {
        setFormErrors(prev => ({ ...prev, email: 'E-mail inválido' }))
      }
    } else if (name === 'confirmEmail' && value) {
      if (value !== formData.email) {
        setFormErrors(prev => ({ ...prev, confirmEmail: 'Os e-mails não coincidem' }))
      }
    } else if (name === 'password' && value) {
      const passwordError = getValidationError('password', value)
      if (passwordError) {
        setFormErrors(prev => ({ ...prev, password: passwordError }))
      }
    } else if (name === 'confirmPassword' && value) {
      if (value !== formData.password) {
        setFormErrors(prev => ({ ...prev, confirmPassword: 'As senhas não coincidem' }))
      }
    }
  }

  const handleCepBlur = async (e) => {
    const rawCep = e.target.value.replace(/\D/g, '');
    if (rawCep.length === 8) {
      setLoadingCep(true);
      const addressData = await lookupCep(rawCep);
      setLoadingCep(false);

      if (addressData) {
        setFormData(prev => ({
          ...prev,
          street: addressData.street,
          neighborhood: addressData.neighborhood,
          city: addressData.city,
          state: addressData.state,
        }));
      } else {
        setFormErrors(prev => ({ ...prev, cep: 'CEP não encontrado ou inválido.' }));
      }
    }
  };

  const validateForm = () => {
    const errors = {}

    if (!formData.main_document) {
      errors.main_document = 'Documento é obrigatório';
    }

    if (!disabledFields.name) {
      if (!formData.name) {
        errors.name = 'Nome é obrigatório'
      } else if (!validateFullName(formData.name)) {
        errors.name = 'Digite seu nome completo'
      }
    }

    if (!disabledFields.birth_date && !formData.birth_date) {
      errors.birth_date = 'Data de nascimento é obrigatória';
    }

    if (!formData.email) {
      errors.email = 'E-mail é obrigatório'
    } else if (!validateEmail(formData.email)) {
      errors.email = 'E-mail inválido'
    }

    if (!formData.confirmEmail) {
      errors.confirmEmail = 'Confirme seu e-mail'
    } else if (formData.email !== formData.confirmEmail) {
      errors.confirmEmail = 'Os e-mails não coincidem'
    }

    if (!formData.password) {
      errors.password = 'Senha é obrigatória'
    } else {
      const passwordError = getValidationError('password', formData.password)
      if (passwordError) errors.password = passwordError
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirme sua senha'
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem'
    }

    // Validação de endereço
    if (!formData.cep || formData.cep.replace(/\D/g, '').length !== 8) {
      errors.cep = 'CEP válido é obrigatório'
    }
    if (!formData.street) {
      errors.street = 'Rua é obrigatória'
    }
    if (!formData.number) {
      errors.number = 'Número é obrigatório'
    }
    if (!formData.neighborhood) {
      errors.neighborhood = 'Bairro é obrigatório'
    }
    if (!formData.city) {
      errors.city = 'Cidade é obrigatória'
    }
    if (!formData.state) {
      errors.state = 'Estado é obrigatório'
    }

    if (!acceptedTerms) {
      errors.terms = 'Você deve aceitar os termos de uso'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)

    // Format birth_date to YYYY-MM-DD for the API
    const fullAddress = `${formData.street}, ${formData.number}${formData.complement ? `, ${formData.complement}` : ''}, ${formData.neighborhood}, ${formData.city}, ${formData.state}`;

    const dataToSend = {
      ...formData,
      cep: formData.cep.replace(/\D/g, ''),
      address: fullAddress,
      slug_city: city?.slug || 'uberlandia'
    };

    if (dataToSend.birth_date) {
      const parts = dataToSend.birth_date.split('/');
      if (parts.length === 3) {
        dataToSend.birth_date = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    const result = await signUp(dataToSend)

    // A navegação será tratada pelo useEffect que observa a mudança em `isAuthenticated`
    // que redirecionará para o perfil do usuário.

    setIsSubmitting(false)
  }

  const handleGoogleSignUp = () => {
    signInWithGoogle()
  }

  if ((loading && !isSubmitting) || isAuthenticated) {
    return (
      <div className="register-page register-page--loading">
        <Loading size="lg" text="Carregando..." />
      </div>
    )
  }

  return (
    <main className="register-page">
      <div className="register-page__container">
        <div className="register-page__content">
          {/* Logo */}
          <Link to={`/${city?.slug || ''}`} className="register-page__logo">
            <img
              src={isRibeirao ? '/icon-rib.png' : '/icon-udi.png'}
              alt="Arqzin Ingressos"
              className="register-page__logo-image"
            />
          </Link>

          <div className="register-page__card">
            <div className="register-page__header">
              <h1 className="register-page__title">Criar conta</h1>
              <p className="register-page__subtitle">
                Cadastre-se para comprar ingressos e acompanhar seus eventos favoritos
              </p>
            </div>

            {/* Erro geral */}
            {error && (
              <div className="register-page__error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {ENABLE_GOOGLE_LOGIN && (
              <>
                <div className="register-page__social">
                  <button
                    type="button"
                    className="register-page__social-btn register-page__social-btn--google"
                    onClick={handleGoogleSignUp}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Cadastrar com Google</span>
                  </button>
                </div>

                <div className="register-page__divider">
                  <span>ou cadastre-se com e-mail</span>
                </div>
              </>
            )}

            {/* Formulário */}
            <form className="register-page__form" onSubmit={handleSubmit}>
              <Dropdown
                label="Tipo de Documento"
                id="document_type"
                name="document_type"
                value={formData.document_type}
                onChange={handleChange}
                options={DISABLE_PASSPORT_REGISTRATION ? DOCUMENT_TYPES.filter(t => t.value !== 'Passaporte') : DOCUMENT_TYPES}
                error={formErrors.document_type}
                disabled={DISABLE_PASSPORT_REGISTRATION}
              />

              <div className="register-page__field">
                <label htmlFor="main_document">Documento Principal</label>
                <input
                  type="text"
                  id="main_document"
                  name="main_document"
                  value={formData.document_type === 'CPF' ? formatCPF(formData.main_document) : formData.main_document}
                  onChange={handleChange}
                  onBlur={() => handleDocumentCheck()}
                  placeholder={formData.document_type === 'CPF' ? '000.000.000-00' : 'Seu documento'}
                  className={formErrors.main_document ? 'input--error' : ''}
                />
                {isCheckingDocument && <Loading size="sm" text="Verificando..." />}
                {formErrors.main_document && (
                  <span className="register-page__field-error">{formErrors.main_document}</span>
                )}
                {documentCheckMessage && (
                  <span className="register-page__field-message">{documentCheckMessage}</span>
                )}
              </div>

              <div className="register-page__field">
                <label htmlFor="name">Nome completo</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome completo"
                  autoComplete="name"
                  disabled={disabledFields.name}
                  className={formErrors.name ? 'input--error' : ''}
                />
                {formErrors.name && (
                  <span className="register-page__field-error">{formErrors.name}</span>
                )}
              </div>

              <div className="register-page__field">
                <label htmlFor="birth_date">Data de Nascimento</label>
                <DatePickerInput
                  id="birth_date"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  disabled={disabledFields.birth_date}
                  className={formErrors.birth_date ? 'input--error' : ''}
                />
                {formErrors.birth_date && (
                  <span className="register-page__field-error">{formErrors.birth_date}</span>
                )}
              </div>

              <div className="register-page__field">
                <label htmlFor="email">E-mail</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onPaste={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  disabled={disabledFields.email}
                  className={formErrors.email ? 'input--error' : ''}
                />
                {formErrors.email && (
                  <span className="register-page__field-error">{formErrors.email}</span>
                )}
              </div>

              <div className="register-page__field">
                <label htmlFor="confirmEmail">Confirmar E-mail</label>
                <input
                  type="email"
                  id="confirmEmail"
                  name="confirmEmail"
                  value={formData.confirmEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onPaste={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  placeholder="Digite seu e-mail novamente"
                  autoComplete="email"
                  disabled={disabledFields.confirmEmail}
                  className={formErrors.confirmEmail ? 'input--error' : ''}
                />
                {formErrors.confirmEmail && (
                  <span className="register-page__field-error">{formErrors.confirmEmail}</span>
                )}
              </div>

              <div className="register-page__field">
                <label htmlFor="password">Senha</label>
                <div className="register-page__password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    disabled={disabledFields.password}
                    className={formErrors.password ? 'input--error' : ''}
                  />
                  <button
                    type="button"
                    className="register-page__password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                <span className="register-page__field-hint">
                  A senha deve ter no mínimo 8 caracteres, incluindo letras maiúsculas, minúsculas e números.
                </span>
                {formErrors.password && (
                  <span className="register-page__field-error">{formErrors.password}</span>
                )}
              </div>

              <div className="register-page__field">
                <label htmlFor="confirmPassword">Confirmar senha</label>
                <div className="register-page__password-input">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Digite a senha novamente"
                    autoComplete="new-password"
                    disabled={disabledFields.confirmPassword}
                    className={formErrors.confirmPassword ? 'input--error' : ''}
                  />
                  <button
                    type="button"
                    className="register-page__password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {formErrors.confirmPassword && (
                  <span className="register-page__field-error">{formErrors.confirmPassword}</span>
                )}
              </div>

              {/* Campos de Endereço */}
              <div className="register-page__divider">
                <span>Endereço</span>
              </div>

              <div className="register-page__row">
                <div className="register-page__field">
                  <label htmlFor="cep">CEP</label>
                  <input
                    type="text"
                    id="cep"
                    name="cep"
                    value={formData.cep}
                    onChange={handleChange}
                    onBlur={handleCepBlur}
                    placeholder="00000-000"
                    maxLength={9}
                    className={formErrors.cep ? 'input--error' : ''}
                  />
                  {loadingCep && <Loading size="sm" text="Buscando..." />}
                  {formErrors.cep && (
                    <span className="register-page__field-error">{formErrors.cep}</span>
                  )}
                </div>
              </div>

              <div className="register-page__field">
                <label htmlFor="street">Rua</label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  placeholder="Nome da rua"
                  className={formErrors.street ? 'input--error' : ''}
                  disabled={loadingCep}
                />
                {formErrors.street && (
                  <span className="register-page__field-error">{formErrors.street}</span>
                )}
              </div>

              <div className="register-page__row">
                <div className="register-page__field">
                  <label htmlFor="number">Número</label>
                  <input
                    type="text"
                    id="number"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    placeholder="123"
                    className={formErrors.number ? 'input--error' : ''}
                  />
                  {formErrors.number && (
                    <span className="register-page__field-error">{formErrors.number}</span>
                  )}
                </div>

                <div className="register-page__field register-mtop">
                  <label htmlFor="complement">Complemento</label>
                  <input
                    type="text"
                    id="complement"
                    name="complement"
                    value={formData.complement}
                    onChange={handleChange}
                    placeholder="Apto, bloco..."
                  />
                </div>
              </div>

              <div className="register-page__field">
                <label htmlFor="neighborhood">Bairro</label>
                <input
                  type="text"
                  id="neighborhood"
                  name="neighborhood"
                  value={formData.neighborhood}
                  onChange={handleChange}
                  placeholder="Nome do bairro"
                  className={formErrors.neighborhood ? 'input--error' : ''}
                  disabled={loadingCep}
                />
                {formErrors.neighborhood && (
                  <span className="register-page__field-error">{formErrors.neighborhood}</span>
                )}
              </div>

              <div className="register-page__row">
                <div className="register-page__field register-mbottom">
                  <label htmlFor="city_input">Cidade</label>
                  <input
                    type="text"
                    id="city_input"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Sua cidade"
                    className={formErrors.city ? 'input--error' : ''}
                    disabled={loadingCep}
                  />
                  {formErrors.city && (
                    <span className="register-page__field-error">{formErrors.city}</span>
                  )}
                </div>

                <Dropdown
                  label="Estado"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  options={[{ value: "", label: "UF" }, ...STATES_OPTIONS]}
                  error={formErrors.state}
                  disabled={loadingCep}
                />
              </div>

              {/* Termos de uso */}
              <div className="register-page__terms">
                <label className="register-page__checkbox">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <span className="register-page__checkbox-mark"></span>
                  <span className="register-page__checkbox-text">
                    Li e aceito os{' '}
                    <Link to={`/${city?.slug || ''}/termos-de-uso`} target="_blank">Termos de Uso</Link>
                    {' '}e a{' '}
                    <Link to={`/${city?.slug || ''}/politica-de-privacidade`} target="_blank">Política de Privacidade</Link>
                  </span>
                </label>
                {formErrors.terms && (
                  <span className="register-page__field-error">{formErrors.terms}</span>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting || isCheckingDocument}
              >
                Criar conta
              </Button>
            </form>

            {/* Link para login */}
            <p className="register-page__login-link">
              Já tem uma conta?{' '}
              <Link to={`/${city?.slug || ''}/login`}>Entrar</Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Register
